import { useQuery } from '@tanstack/react-query';
import { insightsApi } from '../api/insights';
import { Card } from '../components/Card';
import { KpiCard } from '../components/KpiCard';
import { Pill } from '../components/Pill';
import { SeriesChart } from '../components/SeriesChart';
import { DataTable, type Column } from '../components/DataTable';
import { fmtNum } from '../lib/format';
import type { PriceAlertTopCardRow } from '../api/types';

export function PriceAlertsScreen() {
  const overview = useQuery({
    queryKey: ['price-alerts-overview'],
    queryFn: () => insightsApi.priceAlertsOverview(),
  });
  const topCards = useQuery({
    queryKey: ['price-alerts-top-cards', 25],
    queryFn: () => insightsApi.priceAlertsTopCards(25),
  });
  const series = useQuery({
    queryKey: ['price-alerts-series', 30],
    queryFn: () => insightsApi.priceAlertsSeries(30),
  });

  const o = overview.data;
  const above = o?.directionSplit.above ?? 0;
  const below = o?.directionSplit.below ?? 0;
  const dirTotal = above + below;

  const seriesPoints = (series.data ?? []).map((p) => ({
    label: shortDay(p.date),
    value: p.created,
    line: p.triggered,
  }));

  const cardColumns: Column<PriceAlertTopCardRow>[] = [
    {
      key: 'card',
      header: 'Card',
      render: (r) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{r.name ?? r.cardId}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.setName ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'above',
      header: 'Above',
      sortKey: 'above',
      align: 'right',
      hideMobile: true,
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--good)' }}>{fmtNum(r.above)}</span>,
    },
    {
      key: 'below',
      header: 'Below',
      sortKey: 'below',
      align: 'right',
      hideMobile: true,
      render: (r) => <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--warn)' }}>{fmtNum(r.below)}</span>,
    },
    {
      key: 'alertCount',
      header: 'Alerts',
      sortKey: 'alertCount',
      align: 'right',
      render: (r) => <Pill tone="brand" size="sm">{fmtNum(r.alertCount)}</Pill>,
    },
  ];

  if (overview.error) {
    return (
      <div style={{ padding: '24px 28px' }}>
        <Card padding={20}>
          <span style={{ color: 'var(--bad)', fontSize: 13 }}>{(overview.error as Error).message}</span>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 14 }}>
        <KpiCard label="Total alerts" value={o?.totalAlerts ?? 0} accent />
        <KpiCard label="Active alerts" value={o?.activeAlerts ?? 0} />
        <KpiCard
          label="Triggered"
          value={o?.triggeredAlerts ?? 0}
          sublabel={o ? `${o.triggerRatePct.toFixed(1)}% of all alerts` : undefined}
        />
        <KpiCard
          label="Users with alerts"
          value={o?.adoption.usersWithAlerts ?? 0}
          sublabel={o ? `${o.adoption.pctOfPremiumUsers.toFixed(1)}% of premium · ${o.adoption.avgAlertsPerUser.toFixed(1)} avg/user` : undefined}
        />
      </div>

      {/* Direction split */}
      <Card padding={18}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15 }}>Alert direction</div>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>above vs. below target price</span>
        </div>
        <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', background: 'var(--bg-sunken)', marginBottom: 12 }}>
          <div style={{ width: `${dirTotal > 0 ? (above / dirTotal) * 100 : 0}%`, background: 'var(--good)' }} />
          <div style={{ width: `${dirTotal > 0 ? (below / dirTotal) * 100 : 0}%`, background: 'var(--warn)' }} />
        </div>
        <DirRow color="var(--good)" label="Above target (price rose past)" value={above} total={dirTotal} />
        <DirRow color="var(--warn)" label="Below target (price fell past)" value={below} total={dirTotal} />
      </Card>

      <Card padding={18}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>
            Alerts created &amp; triggered · last 30 days
          </div>
          <span style={{ fontSize: 11, color: 'var(--ink-3)', display: 'inline-flex', gap: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--brand-soft)' }} /> created
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--brand-2, var(--brand))' }} /> triggered
            </span>
          </span>
        </div>
        {series.isFetching && seriesPoints.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
        ) : seriesPoints.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>No alert activity yet.</div>
        ) : (
          <SeriesChart
            data={seriesPoints}
            height={260}
            valueLabel="created"
            lineLabel="triggered"
            lineFmt={(n) => fmtNum(n)}
          />
        )}
      </Card>

      <Card padding={0}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Most alerted cards</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            Cards with the most price alerts set · above vs. below breakdown
          </div>
        </div>
        <DataTable
          rows={topCards.data ?? []}
          columns={cardColumns}
          rowKey={(r) => r.cardId}
          loading={topCards.isFetching}
          pageSize={25}
          emptyState="No price alerts set yet."
        />
      </Card>
    </div>
  );
}

function DirRow({ color, label, value, total }: { color: string; label: string; value: number; total: number }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }}>
      <span style={{ fontSize: 13, color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
        {label}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--ink)' }}>
        {fmtNum(value)} <span style={{ color: 'var(--ink-3)' }}>· {pct.toFixed(0)}%</span>
      </span>
    </div>
  );
}

/** "Jul 7" from an ISO date string. */
function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
